/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1783900800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Jul 13 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "yahoo",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Yahoo",
          "depth": 0,
          "id": "connect-to-yahoo"
        }, {
          "value": "Configure Segment credentials",
          "depth": 1,
          "id": "configure-segment-credentials"
        }, {
          "value": "Request and configure Conversion event credentials",
          "depth": 1,
          "id": "request-and-configure-conversion-event-credentials"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing events",
          "depth": 1,
          "id": "syncing-events"
        }, {
          "value": "Syncing segments",
          "depth": 1,
          "id": "syncing-segments"
        }, {
          "value": "Audience type selection",
          "depth": 1,
          "id": "audience-type-selection"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      p: "p",
      h3: "h3",
      ul: "ul",
      li: "li",
      img: "img",
      h4: "h4",
      code: "code",
      pre: "pre",
      h5: "h5",
      ol: "ol"
    }, _provideComponents(), props.components), {EventSyncWarning, MatchBoosterDestination, ArrayFanOutTip, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!ArrayFanOutTip) _missingMdxReference("ArrayFanOutTip", true);
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!MatchBoosterDestination) _missingMdxReference("MatchBoosterDestination", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported sync modes"
            }), _jsx(_components.th, {
              children: "API reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#syncing-events",
                children: _jsx(_components.strong, {
                  children: "Conversion events"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to any events"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://help.yahooinc.com/dsp-api/docs/standard-yahoo-conversion-api",
                children: "Conversions API"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#syncing-segments",
                children: _jsx(_components.strong, {
                  children: "Segments"
                })
              })
            }), _jsx(_components.td, {
              children: "Create and update audiences"
            }), _jsx(_components.td, {
              children: "Add"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developer.yahooinc.com/dsp/api/docs/traffic/audience/about-audience.html",
                children: "Audiences API"
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-yahoo",
        children: "Connect to Yahoo"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Yahoo"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["The Yahoo destination has two independent credential sections: ", _jsx(_components.a, {
          href: "#configure-segment-credentials",
          children: _jsx(_components.strong, {
            children: "Segment integration"
          })
        }), " and ", _jsx(_components.a, {
          href: "#request-and-configure-conversion-event-credentials",
          children: _jsx(_components.strong, {
            children: "Conversion events"
          })
        }), ". Configure whichever section matches the sync type you plan to use."]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["You don't have to complete both sections during setup. For example, you can\nconfigure ", _jsx(_components.strong, {
            children: "Segment integration"
          }), ", save the destination, and come back later\nto add ", _jsx(_components.strong, {
            children: "Conversion events"
          }), " credentials from the destination's\n", _jsx(_components.strong, {
            children: "Configuration"
          }), " tab once Yahoo has replied to your request."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "configure-segment-credentials",
        children: "Configure Segment credentials"
      }), "\n", _jsx(_components.p, {
        children: "For Hightouch to sync data to your segments, you need to enter:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Client ID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Client Secret"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["To retrieve these, open your ", _jsx(_components.a, {
          href: "https://sso.admanagerplus.yahoo.com/app/dashboard/list",
          children: "Yahoo DSP Dashboard"
        }), ". In the upper-right corner, select your name, then ", _jsx(_components.strong, {
          children: "My Account"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-yahoo-dsp-dashboard.png",
          alt: "Yahoo DSP dashboard"
        })
      }), "\n", _jsx(_components.p, {
        children: "Activate the API and agree to the terms.\nA success message displays your credentials.\nCopy the client ID and secret, and keep it in a safe place."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-yahoo-credentials.png",
          alt: "Yahoo credentials"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For more details, visit the ", _jsx(_components.a, {
          href: "https://developer.yahooinc.com/dsp/api/docs/authentication/vmdn-auth-overview.html?guccounter=1&guce_referrer=aHR0cHM6Ly93d3cuZ29vZ2xlLmNvbS8&guce_referrer_sig=AQAAABxrNZzuVEB2pL8Fdf9cjsTku2w7ooTz2Mz7BCA_InEazpCygsScZNhzJVJ4SJypKZzOTik4_avcNoAAspLZaQ6nEuoKvVg8OUVL6VMTkOdeVAup900GJ8S_ql543N_0WRsyyQgpAr41kcwJpHGDYLIQjtfViMeWaVYod_mxkmhR",
          children: "Yahoo DSP Authentication docs"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "request-and-configure-conversion-event-credentials",
        children: "Request and configure Conversion event credentials"
      }), "\n", _jsx(_components.p, {
        children: "The Yahoo Conversion API requires approval from both Yahoo and Hightouch before you can configure it."
      }), "\n", _jsx(_components.h4, {
        id: "step-1-gather-your-yahoo-dsp-identifiers",
        children: "Step 1: Gather your Yahoo DSP identifiers"
      }), "\n", _jsx(_components.p, {
        children: "You need this information from your Yahoo DSP account before you contact Yahoo:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Client name"
          }), ": Your advertiser name, found on your advertiser profile page in Yahoo DSP."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Pixel ID"
          }), ": The ID of the pixel you'll use for the Conversion API. To find or create one, go to your advertiser in Yahoo DSP, open the ", _jsx(_components.strong, {
            children: "Tracking"
          }), " tab, and select ", _jsx(_components.strong, {
            children: "Get Pixel Code"
          }), ". This lists all pixels associated with your account. If you don't have one yet, select ", _jsx(_components.strong, {
            children: "Create New Pixel"
          }), ", name it, and save it. The pixel ID appears in the list and is also embedded in the full pixel tag."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "MDM ID"
          }), ": Your advertiser's MDM ID, if you know it. This is optional."]
        }), "\n"]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Use a pixel dedicated to the Conversion API only. Don't share it with other\ntracking methods."
        })
      }), "\n", _jsx(_components.h4, {
        id: "step-2-request-credentials-from-yahoo",
        children: "Step 2: Request credentials from Yahoo"
      }), "\n", _jsxs(_components.p, {
        children: ["In the destination's ", _jsx(_components.strong, {
          children: "Conversion events"
        }), " section, download the ", _jsx(_components.strong, {
          children: "Hightouch public key"
        }), ". Then select ", _jsx(_components.strong, {
          children: "Email Yahoo team"
        }), " to open a pre-filled email addressed to ", _jsx(_components.code, {
          children: "technical.solutions@yahooinc.com"
        }), " with the public key attached:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "Subject: Yahoo CAPI client credentials request\n\nHi Yahoo Team,\n\nI'm interested in using the Yahoo Conversions API (CAPI) integration through Hightouch and would like to request that client credentials be generated for our account.\n\nClient Name: <Enter your client name>\nPixel ID: <Enter your pixel ID>\nMDM ID: <Optional: Enter your MDM ID, if available>\n\nFor your convenience, the Hightouch public key is attached.\n\nPlease let me know if you need any additional information to complete the setup.\n\nThank you,\n"
        })
      }), "\n", _jsx(_components.p, {
        children: "You can also send this request yourself instead of using the pre-filled draft. Yahoo encrypts your credentials with the public key and sends back an encrypted credentials file."
      }), "\n", _jsx(_components.h4, {
        id: "step-3-upload-your-credentials-to-hightouch",
        children: "Step 3: Upload your credentials to Hightouch"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you receive the encrypted credentials file from Yahoo, upload it in the ", _jsx(_components.strong, {
          children: "Conversion events"
        }), " section –– either in the setup flow or in the destination's ", _jsx(_components.strong, {
          children: "Configuration"
        }), " tab."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Yahoo destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Yahoo destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-events",
        children: "Syncing events"
      }), "\n", _jsxs(_components.p, {
        children: ["When syncing events, Hightouch treats any records added to ", _jsx(_components.a, {
          href: "/getting-started/concepts#sources",
          children: "your source"
        }), " as new events and sends them to Yahoo DSP when your sync runs."]
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h4, {
        id: "pixel-id",
        children: "Pixel ID"
      }), "\n", _jsxs(_components.p, {
        children: ["You must set a Pixel ID when syncing conversion events to Yahoo DSP. ", _jsx(_components.a, {
          href: "https://help.yahooinc.com/dsp-api/docs/standard-yahoo-conversion-api#create-a-pixel-id-yp-id-in-the-yahoo-dsp",
          children: "Create a new Pixel ID"
        }), " in Yahoo DSP under the tracking tab for your advertiser in Yahoo DSP."]
      }), "\n", _jsx(_components.p, {
        children: "You can also map a model column to the Pixel ID field instead of entering a static value. This lets a single sync route events to different pixels, based on a value in each row."
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["The Pixel ID used for CAPI should be unique and ", _jsx(_components.strong, {
            children: "only"
          }), " used for CAPI."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "choose-a-delivery-method",
        children: "Choose a delivery method"
      }), "\n", _jsx(_components.p, {
        children: "Yahoo CAPI accepts conversion events through both a streaming and a batch endpoint."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Batch"
          }), " (recommended): Yahoo processes batch events once a day. Use batch delivery when you're sending more than 100,000 events. The batch endpoint doesn't return an indication of rejected rows."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Streaming"
          }), ": Yahoo processes streamed events multiple times a day. Streaming works best for smaller event loads."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "event-timestamp",
        children: "Event timestamp"
      }), "\n", _jsx(_components.p, {
        children: "You can include a timestamp of when the event occurred. If this field is empty, Hightouch uses the time the event arrives at the server."
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch converts any ", _jsx(_components.code, {
          children: "DateTime"
        }), " values to Epoch UTC milliseconds per Yahoo DSP's expectations."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Each event must include at least one user identifier. This can be either one of the ", _jsx(_components.code, {
          children: "userData"
        }), " fields (email, phone, Android advertising ID, Apple advertising ID, or 3rd party external identifier) or a click ID (Yahoo Click ID ", _jsx(_components.code, {
          children: "vmcid"
        }), " or Taboola Click ID ", _jsx(_components.code, {
          children: "tblci"
        }), "). Ensure that at least one of these fields is included in your mapped fields; Hightouch rejects rows where all mapped identifier fields are empty, since Yahoo DSP would reject the event."]
      }), "\n", _jsxs(_components.p, {
        children: ["Yahoo treats the two click IDs as mutually exclusive—don't map both ", _jsx(_components.code, {
          children: "vmcid"
        }), " and ", _jsx(_components.code, {
          children: "tblci"
        }), " with values on the same event.\nFor more information about required and supported parameters, refer to ", _jsx(_components.a, {
          href: "https://help.yahooinc.com/dsp-api/docs/standard-yahoo-conversion-api#capi-payload",
          children: "Yahoo's Conversion API documentation"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "automatically-hash-pii",
        children: "Automatically hash PII"
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch detects and hashes values for fields that Yahoo requires to be hashed, including ", _jsx(_components.code, {
          children: "userData.email"
        }), " and ", _jsx(_components.code, {
          children: "userData.phone"
        }), ". Turn this off if you're already sending hashed values."]
      }), "\n", _jsx(_components.h4, {
        id: "rate-limits",
        children: "Rate limits"
      }), "\n", _jsxs(_components.p, {
        children: ["Yahoo CAPI's default rate limits are 200 requests per second and 10 MB of data per second on the batch endpoint. If Yahoo has enabled a higher rate limit for your pixel, expand ", _jsx(_components.strong, {
          children: "Advanced"
        }), " in the sync configuration and enter it in the rate limit field. Hightouch adjusts its send behavior accordingly."]
      }), "\n", _jsx(_components.h4, {
        id: "configure-conversions-in-yahoo-dsp",
        children: "Configure conversions in Yahoo DSP"
      }), "\n", _jsxs(_components.p, {
        children: ["After Hightouch begins syncing conversion events to your pixel, configure how Yahoo DSP interprets that data. Create ", _jsx(_components.strong, {
          children: "pixel rules"
        }), " in the Yahoo DSP UI to segment the incoming event stream into named conversions you can attach to campaigns."]
      }), "\n", _jsx(_components.h5, {
        id: "create-a-pixel-rule",
        children: "Create a pixel rule"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In Yahoo DSP, go to your advertiser and open the ", _jsx(_components.strong, {
            children: "Tracking"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the pixel you're using for CAPI, then select ", _jsx(_components.strong, {
            children: "Create Rule"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Name the rule and associate it with your pixel."
        }), "\n", _jsxs(_components.li, {
          children: ["Set the ", _jsx(_components.strong, {
            children: "goal type"
          }), " — for example, ", _jsx(_components.code, {
            children: "Purchase"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Enter a placeholder value, such as ", _jsx(_components.code, {
            children: "1"
          }), ", in the value field. If you pass a dynamic value in your Hightouch sync, it overrides this placeholder at the event level."]
        }), "\n"]
      }), "\n", _jsx(_components.h5, {
        id: "define-rule-conditions",
        children: "Define rule conditions"
      }), "\n", _jsx(_components.p, {
        children: "Pixel rules filter incoming events by matching on event data fields:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Yahoo DSP field"
            }), _jsx(_components.th, {
              children: "Maps from"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Category"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "category"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Label"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "subcategory"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Action"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "eventName"
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "You can create conditions using these operators:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Equals"
          }), ": works with strings and numbers."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Contains"
          }), ": works with strings."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Less than"
          }), " / ", _jsx(_components.strong, {
            children: "Greater than"
          }), ": works with numbers only."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Regex"
          }), ": matches one of several values. Pipe-delimit values inside parentheses, for example ", _jsx(_components.code, {
            children: "(purchase|checkout|order_complete)"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h5, {
        id: "set-audience-membership-recency",
        children: "Set audience membership recency"
      }), "\n", _jsx(_components.p, {
        children: "Each rule automatically creates an audience segment. You can configure how recently a user must meet the rule's conditions to be included — for example, at least once in the last 30 days, or at least three times in the last 15 days. The time unit can be hours or days. This recency window controls audience membership, not conversion attribution look-back."
      }), "\n", _jsx(_components.h5, {
        id: "attach-the-rule-to-a-campaign-line",
        children: "Attach the rule to a campaign line"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Open the relevant campaign in Yahoo DSP and go to the line item."
        }), "\n", _jsxs(_components.li, {
          children: ["In ", _jsx(_components.strong, {
            children: "Line Properties"
          }), ", set a ", _jsx(_components.strong, {
            children: "Goal Type"
          }), " and select your pixel rule."]
        }), "\n", _jsx(_components.li, {
          children: "Set your click and view look-back windows. Both default to 7 days, with a range of 1 hour to 30 days."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-segments",
        children: "Syncing segments"
      }), "\n", _jsx(_components.p, {
        children: "The Yahoo DSP enables you to build audiences that are most responsive to your campaigns, are most likely to convert, or are otherwise advantageous."
      }), "\n", _jsx(_components.p, {
        children: "Begin by selecting the Yahoo account you would like to sync to.\nThen, select whether to create a new audience or use an existing one.\nWhen creating a new audience, you can optionally enter a name; otherwise, Hightouch defaults to the name of the associated model. To use an existing audience, select the desired audience from the dropdown."
      }), "\n", _jsx(_components.h3, {
        id: "audience-type-selection",
        children: "Audience type selection"
      }), "\n", _jsx(_components.p, {
        children: "Yahoo DSP API allows you to create different types of audiences.\nHightouch supports email address, device ID, and IP address types.\nYou can only select one audience type per sync.\nYou can later combine the different audience types when building your campaigns in Yahoo."
      }), "\n", _jsx(_components.h4, {
        id: "user-identifiers",
        children: "User identifiers"
      }), "\n", _jsx(_components.p, {
        children: "To identify which users to add or update in an audience, select model columns and the corresponding Yahoo fields.\nThe available fields vary depending on your selected audience type."
      }), "\n", _jsx(MatchBoosterDestination, {
        destinationName: "Yahoo Demand Side Platform (DSP) audiences"
      }), "\n", _jsx(ArrayFanOutTip, {
        destinationName: "Yahoo DSP"
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Yahoo DSP's API doesn't support removing identifiers or audience members."
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
